/**
 * Scroll the actual page owner(s) to the absolute top.
 * The dashboard uses window scrolling, but some individual pages may have
 * their own overflow containers. Sidebar, header menus and dialogs are left alone.
 */
const isChrome = typeof document !== "undefined";

const isNavigationOrOverlay = (element: HTMLElement): boolean => {
  if (element.closest('aside, nav, [role="navigation"], [role="dialog"], [aria-modal="true"], .chakra-drawer__content, [data-pickdengu-preserve-scroll="true"]')) {
    return true;
  }

  let node: HTMLElement | null = element;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    // Sidebar and floating menus often use fixed or sticky positioning.
    if (style.position === "fixed" || style.position === "sticky") return true;
    node = node.parentElement;
  }
  return false;
};

const moveToTopImmediately = (element: HTMLElement): void => {
  const original = element.style.getPropertyValue("scroll-behavior");
  const priority = element.style.getPropertyPriority("scroll-behavior");
  element.style.setProperty("scroll-behavior", "auto", "important");
  try {
    element.scrollTop = 0;
    element.scrollLeft = 0;
    element.scrollTo({ top: 0, left: 0, behavior: "auto" });
  } catch {
    element.scrollTop = 0;
    element.scrollLeft = 0;
  } finally {
    if (original) element.style.setProperty("scroll-behavior", original, priority);
    else element.style.removeProperty("scroll-behavior");
  }
};

export const scrollPageToTop = (): void => {
  if (!isChrome) return;

  const root = document.documentElement;
  const body = document.body;
  const owner = document.scrollingElement as HTMLElement | null;
  const targets = new Set<HTMLElement>([root, body]);
  if (owner) targets.add(owner);

  // Find real content scroll owners rather than assuming the window owns it.
  // Only elements currently scrolled need resetting.
  document.querySelectorAll<HTMLElement>("*").forEach((element) => {
    if (
      element.scrollTop > 0 &&
      element.scrollHeight > element.clientHeight + 1 &&
      !isNavigationOrOverlay(element)
    ) {
      targets.add(element);
    }
  });

  targets.forEach(moveToTopImmediately);
};
